import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, CheckCircle2, CreditCard, ShieldCheck, MessageCircle, Clock } from "lucide-react";
import { payPublic, payCheckout, payStatus, eur } from "../lib/payRequestsApi";

const WA_POZZO = "393513120603";

export default function PayRequestPage() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const fromStripe = new URLSearchParams(window.location.search).get("paid") === "1";

  useEffect(() => {
    payPublic(token).then(setD).catch(() => setErr("Link non valido o richiesta non più disponibile."));
  }, [token]);
  useEffect(() => {
    if (!fromStripe || !d || d.status === "paid") return;
    let tries = 0;
    const t = setInterval(async () => {
      tries += 1;
      const r = await payStatus(token).catch(() => null);
      if (r?.status === "paid" || tries > 8) { clearInterval(t); if (r?.status) setD((cur) => ({ ...cur, status: r.status })); }
    }, 1500);
    return () => clearInterval(t);
  }, [fromStripe, d, token]);

  const pay = async () => {
    setBusy(true);
    try {
      const r = await payCheckout(token);
      if (r.checkout_url) { window.location.href = r.checkout_url; return; }
      if (r.status === "paid") setD({ ...d, status: "paid" });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Pagamento non avviato, riprova.");
    } finally { setBusy(false); }
  };

  if (err) return <div className="min-h-screen bg-[#f7ebdf] grid place-items-center p-6 text-center text-[#4a1c17]" data-testid="pay-error">{err}</div>;
  if (!d) return <div className="min-h-screen bg-[#f7ebdf] grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#4a1c17]" /></div>;

  const first = (d.customer_name || "").split(" ")[0];
  const wa = `https://wa.me/${WA_POZZO}?text=${encodeURIComponent(`Buongiorno, ho una domanda sulla richiesta di pagamento ${d.number}.`)}`;
  return (
    <div className="min-h-screen bg-[#f7ebdf] text-[#2b1410] px-4 py-10" data-testid="pay-page">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-6">
          <div className="font-serif text-2xl font-semibold text-[#4a1c17]">Il Pozzo dei Giganti</div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#7c5a4e] mt-1">Richiesta di pagamento · {d.number}</div>
        </div>
        <div className="bg-white rounded-3xl border border-[#e8dcc8] shadow-sm p-6 space-y-5">
          {d.status === "paid" ? (
            <div className="text-center space-y-2" data-testid="pay-paid">
              <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
              <div className="font-serif text-xl font-semibold">Pagamento ricevuto, grazie {first}!</div>
              <div className="text-sm text-[#7c5a4e]">{d.description} · {eur(d.amount_cents)}. La ricevuta arriva via email da Stripe. A presto al Pozzo.</div>
            </div>
          ) : fromStripe ? (
            <div className="text-center space-y-2" data-testid="pay-checking"><Loader2 className="h-8 w-8 animate-spin mx-auto text-[#4a1c17]" /><div className="text-sm text-[#7c5a4e]">Stiamo confermando il pagamento…</div></div>
          ) : d.status === "expired" ? (
            <div className="text-center space-y-2" data-testid="pay-expired"><Clock className="h-10 w-10 mx-auto text-stone-400" /><div className="font-semibold">Questo link è scaduto</div><div className="text-sm text-[#7c5a4e]">Scrivici e te ne mandiamo uno nuovo.</div></div>
          ) : (
            <>
              <div className="text-sm text-[#7c5a4e]">Ciao {first}, ecco il riepilogo del servizio da saldare:</div>
              <div className="rounded-2xl bg-[#faf3ee] p-4">
                <div className="font-medium" data-testid="pay-description">{d.description}</div>
                <div className="mt-3 flex items-end justify-between"><span className="text-xs uppercase tracking-wide text-[#7c5a4e]">Importo</span><span className="font-serif text-3xl font-semibold text-[#4a1c17]" data-testid="pay-amount">{eur(d.amount_cents)}</span></div>
              </div>
              <button onClick={pay} disabled={busy} className="w-full h-12 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="pay-button">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Paga con carta
              </button>
              <div className="text-[11px] text-[#7c5a4e] flex items-center justify-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Pagamento sicuro con Stripe · carte, Apple Pay e Google Pay</div>
            </>
          )}
          <a href={wa} className="flex items-center justify-center gap-1.5 text-xs text-[#4a1c17] underline underline-offset-2" data-testid="pay-wa"><MessageCircle className="h-3.5 w-3.5" /> Hai una domanda? Scrivici su WhatsApp</a>
        </div>
      </div>
    </div>
  );
}
